import type { MedChronTemplate, VisitDisplay, VisitTypeFilterOption } from "../types";
import { formatVisitType } from "./visitFormatting";

/** Lists only the types present in visits, so template types with no visits stay out of the checklist. */
export const buildVisitTypeFilterOptions = (
  visits: VisitDisplay[],
  template: MedChronTemplate | null
): VisitTypeFilterOption[] => {
  const visitCountByTypeId = new Map<string, number>();
  for (const visit of visits) {
    visitCountByTypeId.set(
      visit.visit_type_id,
      (visitCountByTypeId.get(visit.visit_type_id) ?? 0) + 1
    );
  }
  return Array.from(visitCountByTypeId, ([visitTypeId, visitCount]) => ({
    visitTypeId,
    label: formatVisitType(template, visitTypeId),
    visitCount,
  })).sort((a, b) => a.label.localeCompare(b.label));
};

export const excludeHiddenVisitTypes = (
  visits: VisitDisplay[],
  hiddenVisitTypeIds: string[]
): VisitDisplay[] => {
  if (hiddenVisitTypeIds.length === 0) return visits;
  const hiddenIdSet = new Set(hiddenVisitTypeIds);
  return visits.filter((visit) => !hiddenIdSet.has(visit.visit_type_id));
};

export const toggleVisitTypeHidden = (
  hiddenVisitTypeIds: string[],
  visitTypeId: string
): string[] =>
  hiddenVisitTypeIds.includes(visitTypeId)
    ? hiddenVisitTypeIds.filter((id) => id !== visitTypeId)
    : [...hiddenVisitTypeIds, visitTypeId];

/** Leaves ids outside visitTypeIds untouched, so other cases' hidden types survive a select-all. */
const withoutVisitTypes = (hiddenVisitTypeIds: string[], visitTypeIds: string[]): string[] => {
  const targetIdSet = new Set(visitTypeIds);
  return hiddenVisitTypeIds.filter((id) => !targetIdSet.has(id));
};

export const hideVisitTypes = (hiddenVisitTypeIds: string[], visitTypeIds: string[]): string[] => [
  ...withoutVisitTypes(hiddenVisitTypeIds, visitTypeIds),
  ...visitTypeIds,
];

export const showVisitTypes = (hiddenVisitTypeIds: string[], visitTypeIds: string[]): string[] =>
  withoutVisitTypes(hiddenVisitTypeIds, visitTypeIds);
